import { Button, Tooltip } from '@atom/mui';
import colors from '@atom/styles/colors';
import { FieldViewType } from '@atom/types/dataType';
import {
  isOverListOptionLimit,
  MAX_LIST_OPTIONS,
} from '@atom/utilities/workOrderFieldUtilities';

import classes from './fieldViewAsToggle.module.css';

type FieldViewAsToggleProps = {
  viewAs: FieldViewType | null;
  onChange: (viewAs: FieldViewType | null) => void;
  listDisabled?: boolean;
  optionCount?: number;
  dataCy?: string;
};

export const LIST_DISABLED_TOOLTIP =
  'List view is not supported for cascading dropdowns.';

export const LIST_OPTION_LIMIT_TOOLTIP = `List doesn't support more than ${MAX_LIST_OPTIONS} options.`;

const OPTIONS = [
  { label: 'Dropdown', value: null },
  { label: 'List', value: FieldViewType.CHECKBOX },
];

const getOptionSx = (selected: boolean) => {
  const color = selected ? colors.utility.activeBlue : colors.neutral.dim;
  const backgroundColor = selected
    ? colors.utility.lightBlueHighlight
    : 'transparent';

  return {
    minWidth: 0,
    padding: '0.25rem 1rem',
    borderRadius: 0,
    fontSize: 'var(--fs-md)',
    lineHeight: 'var(--lh-lg)',
    textTransform: 'none',
    color,
    backgroundColor,
    '&:hover': {
      backgroundColor: selected
        ? colors.utility.lightBlueHighlight
        : colors.utility.highlight,
    },
    '&.Mui-disabled': { color, backgroundColor, opacity: 0.5 },
  };
};

const FieldViewAsToggle = ({
  viewAs,
  onChange,
  listDisabled = false,
  optionCount = 0,
  dataCy = 'fieldViewAsToggle',
}: FieldViewAsToggleProps) => {
  const effectiveViewAs = listDisabled ? null : (viewAs ?? null);

  // A list over the option limit stays selected, so the field can't save until
  // options are removed or it's switched to Dropdown
  const listTooltip = listDisabled
    ? LIST_DISABLED_TOOLTIP
    : isOverListOptionLimit(optionCount)
      ? LIST_OPTION_LIMIT_TOOLTIP
      : '';

  return (
    <div className={classes.container} data-cy={dataCy}>
      <div className={classes.label}>Display as:</div>
      <div className={classes.options}>
        {OPTIONS.map(option => {
          const disabled = option.value !== null && !!listTooltip;
          const selected = effectiveViewAs === option.value;

          return (
            <Tooltip key={option.label} title={disabled ? listTooltip : ''}>
              <span className={classes.option}>
                <Button
                  onClick={() => onChange(option.value)}
                  aria-pressed={selected}
                  disabled={disabled}
                  sx={getOptionSx(selected)}
                  data-cy={`${dataCy}-${option.label.toLowerCase()}`}
                >
                  {option.label}
                </Button>
              </span>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default FieldViewAsToggle;
